import {
  IconArrowDown,
  IconEdit,
  IconRefresh,
  IconReport,
  IconShare,
  IconTransfer
} from "@tabler/icons-react";

import { C_PROPERTIES_STATUS } from "@/src-refactor/modules/shared/model/const/property/real-estate-status";
import { useNavigate } from "react-router-dom";
import MxMenuSocial from "@rf-l1/MxMenuSocial";
import { MxMenuItemProps } from "@rf-l1/MxMenu";
import { Grid, MenuItem } from "@mui/material";
import MxTypography from "@rf-l1/MxTypography";
import MxIcons from "@rf-l1/MxIcons";
import { EFontFamilies } from "@/shared/theme/theme";
import usePermissionProperty from "@rf/property/module/hooks/usePermissionProperty/usePermissionProperty";
import { PropertyItemList } from "@/src-refactor/modules/store/qrtk/services/realestate/responses/property-list.response";
import useReadOnlyModeGuard from "@rf-hooks/useReadOnlyModeGuard";

/**
 * Hook for determining property action permissions
 */
const usePropertyActionsPermissions = () => {
  const navigate = useNavigate();
  const { getPermissions } = usePermissionProperty();
  const { guardReadOnlyAction } = useReadOnlyModeGuard();

  /**
   * Get available actions for a property based on user permissions
   */
  const getOptions = (item: PropertyItemList): MxMenuItemProps[] => {
    const permissions = getPermissions({
      idStatusProperty: item.status_id,
      isProject: Boolean(item.project),
      agents: item.agents.map(agent => ({
        id: agent.id,
        agencyId: agent.agency_id
      }))
    });

    const actions: MxMenuItemProps[] = [
      {
        label: "Ver detalle",
        icon: <IconEdit size={16} />,
        onClick: () => handleShowDetail(item.id),
        show: permissions.canViewProperty
      },
      {
        label: "Editar",
        icon: <IconEdit size={16} />,
        onClick: () => handleEditProperty(item),
        show: permissions.canUpdateProperty
      },
      {
        label: "Reactivar",
        icon: <IconRefresh size={16} />,
        onClick: () =>
          guardReadOnlyAction("reactivar propiedad", "property-list-actions"),
        show: permissions.canReactivateProperty
      },
      {
        label: "Dar de baja",
        icon: <IconArrowDown size={16} />,
        onClick: () =>
          guardReadOnlyAction("dar de baja propiedad", "property-list-actions"),
        show: permissions.canDropProperty
      },
      {
        label: "Referir propiedad",
        icon: <IconTransfer size={16} />,
        onClick: () => guardReadOnlyAction("referir propiedad", "property-list-actions"),
        show: permissions.canReferProperty
      },
      {
        label: "Reportar comisión",
        icon: <IconReport size={16} />,
        onClick: () => handleReportCommission(item.id),
        show: permissions.canReportCommission
      },
      {
        label: "Compartir por redes sociales",
        icon: <IconShare size={16} />,
        onClick: (e: any) => {
          e.preventDefault();
          // handleShareSocial(item);
        },
        template: (
          <MxMenuSocial
            propertyType={item.realstate_type}
            bussinesType={item.business_type}
            sector={item.sector}
            propertyId={item.id.toString()}
          >
            <MenuItem>
              <Grid
                container
                alignItems={"center"}
                justifyContent={"center"}
                spacing={1}
              >
                <Grid item>
                  <MxIcons children={<IconShare size={12} />} color={"grey.600"} />
                </Grid>
                <Grid item>
                  <MxTypography
                    variant="caption"
                    color={"grey.700"}
                    fontWeight={500}
                    fontFamily={EFontFamilies.DISPLAY}
                    sx={{ fontSize: "12px !important", letterSpacing: 0.5 }}
                  >
                    Compartir por redes sociales
                  </MxTypography>
                </Grid>
              </Grid>
            </MenuItem>
          </MxMenuSocial>
        ),
        show: permissions.canShareProperty
      }
    ];

    return actions.filter(action => action.show);
  };

  /**
   * Navigate to property edit page based on property type
   */
  const handleEditProperty = (item: PropertyItemList): void => {
    guardReadOnlyAction("editar propiedad", "property-list-actions");
  };

  const handleShowDetail = (id: number) => {
    const path = `/realestates/detail/${id}`;
    if (window.innerWidth > 768) {
      window.open(path, "_blank");
    } else {
      navigate(path);
    }
  };

  /**
   * Handle report commission action
   */
  const handleReportCommission = (propertyId: number): void => {
    guardReadOnlyAction("reportar comisión", "property-list-actions");
  };

  /**
   * Get user permissions for property actions
   */

  return { getOptions, handleShowDetail };
};

/**
 * Property status flags interface
 */
interface PropertyStatusFlags {
  active: boolean;
  negotiation: boolean;
  inconflict: boolean;
  dropped: boolean;
  pending: boolean;
}

/**
 * Extract property status flags
 */
const getPropertyStatus = (item: PropertyItemList): PropertyStatusFlags => {
  return {
    active: item.status_id === C_PROPERTIES_STATUS.ACTIVE.id,
    negotiation: item.status_id === C_PROPERTIES_STATUS.INNEGOTIATION.id,
    inconflict: item.status_id === C_PROPERTIES_STATUS.INCONFLICT.id,
    dropped: item.status_id === C_PROPERTIES_STATUS.DROPPED.id,
    pending: item.status_id === C_PROPERTIES_STATUS.PENDING.id
  };
};

export default usePropertyActionsPermissions;
